import React, { useState, useEffect, useCallback } from "react";
import { api } from "../utils/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useToast } from "../hooks/use-toast";
import { Lock, Users, MousePointerClick, Mail, LogOut } from "lucide-react";

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
};

const Admin = () => {
  const { toast } = useToast();
  const [passcode, setPasscode] = useState(sessionStorage.getItem("kr_admin_pass") || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({ totalLeads: 0, totalReferrals: 0, subscribers: 0 });
  const [loading, setLoading] = useState(false);

  const headers = useCallback((pass) => ({ headers: { "X-Admin-Passcode": pass } }), []);

  const loadAll = useCallback(
    async (pass) => {
      setLoading(true);
      try {
        const [s, l, r] = await Promise.all([
          api.get("/admin/stats", headers(pass)),
          api.get("/admin/leads", headers(pass)),
          api.get("/admin/referrals", headers(pass)),
        ]);
        setStats(s.data);
        setLeads(l.data);
        setReferrals(r.data);
        setAuthed(true);
      } catch (e) {
        setAuthed(false);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [headers]
  );

  useEffect(() => {
    if (passcode) {
      loadAll(passcode).catch(() => {
        sessionStorage.removeItem("kr_admin_pass");
        setPasscode("");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await loadAll(input);
      sessionStorage.setItem("kr_admin_pass", input);
      setPasscode(input);
      toast({ title: "Welcome back, Kristy!" });
    } catch {
      toast({ title: "Incorrect passcode", description: "Please try again." });
    }
  };

  const logout = () => {
    sessionStorage.removeItem("kr_admin_pass");
    setPasscode("");
    setAuthed(false);
    setInput("");
  };

  if (!authed) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-[#f4f1ea] px-6">
        <form
          onSubmit={handleLogin}
          className="bg-white rounded-3xl p-9 md:p-11 border border-[#efe6d6] shadow-[0_30px_70px_-45px_rgba(84,64,34,0.5)] w-full max-w-md"
        >
          <div className="w-12 h-12 rounded-full bg-[#f7edcf] flex items-center justify-center mb-5">
            <Lock className="w-5 h-5 text-gold" />
          </div>
          <h1 className="font-display text-3xl text-[#141210] mb-2">Agent Dashboard</h1>
          <p className="text-sm text-[#6b6154] mb-7">
            Enter your passcode to view leads and Sharon referrals.
          </p>
          <div className="space-y-2 mb-6">
            <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Passcode</Label>
            <Input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="••••••••"
              className="bg-[#faf7f2] border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#d4af37] hover:bg-[#c19a2e] text-white rounded-full h-12 tracking-luxury uppercase text-xs"
          >
            {loading ? "Checking..." : "Enter Dashboard"}
          </Button>
        </form>
      </div>
    );
  }

  const statCards = [
    { icon: Users, label: "Total Leads", value: stats.totalLeads },
    { icon: MousePointerClick, label: "Sharon Referrals", value: stats.totalReferrals },
    { icon: Mail, label: "Email Subscribers", value: stats.subscribers },
  ];

  return (
    <div className="bg-[#faf7f2] min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-14">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-display text-4xl text-[#141210]">Agent Dashboard</h1>
            <p className="text-sm text-[#6b6154] mt-1">Leads & lender referral tracking</p>
          </div>
          <Button
            onClick={logout}
            variant="outline"
            className="border-[#d8c9ac] text-[#141210] hover:bg-[#f7edcf] rounded-full h-11 tracking-luxury uppercase text-xs"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log Out
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-7 border border-[#efe6d6] flex items-center gap-5">
              <div className="w-14 h-14 rounded-full bg-[#f7edcf] flex items-center justify-center">
                <s.icon className="w-6 h-6 text-gold" />
              </div>
              <div>
                <div className="font-display text-4xl text-[#141210]">{s.value}</div>
                <div className="text-xs tracking-luxury uppercase text-[#6b6154] mt-1">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="leads">
          <TabsList className="bg-[#efe6d6]">
            <TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger>
            <TabsTrigger value="referrals">Sharon Referrals ({referrals.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-6">
            <div className="bg-white rounded-2xl border border-[#efe6d6] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#f4f1ea]">
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Intent</TableHead>
                    <TableHead>Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-[#9a8f7d] py-10">
                        No leads yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    leads.map((l) => (
                      <TableRow key={l.id}>
                        <TableCell className="whitespace-nowrap text-xs">{fmtDate(l.created_at)}</TableCell>
                        <TableCell className="font-medium">{l.name}</TableCell>
                        <TableCell>{l.email}</TableCell>
                        <TableCell className="whitespace-nowrap">{l.phone}</TableCell>
                        <TableCell className="capitalize">{l.type}</TableCell>
                        <TableCell className="capitalize">{l.intent}</TableCell>
                        <TableCell className="max-w-[240px] truncate text-[#6b6154]">{l.message}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="referrals" className="mt-6">
            <div className="bg-white rounded-2xl border border-[#efe6d6] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#f4f1ea]">
                    <TableHead>Date</TableHead>
                    <TableHead>Lender</TableHead>
                    <TableHead>Name (if known)</TableHead>
                    <TableHead>Email (if known)</TableHead>
                    <TableHead>From Page</TableHead>
                    <TableHead>Visitor ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-[#9a8f7d] py-10">
                        No referral click-throughs yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    referrals.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap text-xs">{fmtDate(r.created_at)}</TableCell>
                        <TableCell className="font-medium">{r.lender}</TableCell>
                        <TableCell>{r.name || <span className="text-[#c4b79f]">—</span>}</TableCell>
                        <TableCell>{r.email || <span className="text-[#c4b79f]">—</span>}</TableCell>
                        <TableCell className="text-[#6b6154]">{r.page}</TableCell>
                        <TableCell className="text-[10px] text-[#b3a690]">{r.visitor_id}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
